// ─────────────────────────────────────────────────────────────────────────────
// The cropper's maths, with no DOM in it, so every number the picture editor
// shows or exports can be tested on its own.
//
// Everything is measured in OUTPUT pixels. The frame is the finished picture
// (640 × 640 for a WhatsApp profile); the source is the picture as decoded.
// Where the picture sits is three numbers:
//
//   zoom      1 is "fit": the whole picture inside the frame. Larger is closer.
//   offsetX   where the picture's centre is, measured from the frame's centre,
//   offsetY   in output pixels. 0, 0 is centred.
//
// The scale (output pixels per source pixel) is fitScale × zoom. On screen the
// frame is drawn at whatever size fits, so the component multiplies by one
// more factor (display pixels per output pixel) and nothing here changes.
// ─────────────────────────────────────────────────────────────────────────────

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** Where the picture sits in the frame. */
export interface CropState {
  /** 1 is "fit": the whole picture inside the frame. */
  zoom: number;
  /** The picture's centre minus the frame's centre, in output pixels. */
  offsetX: number;
  offsetY: number;
}

/** The shape the result is shown in. A circle is how WhatsApp shows a profile picture. */
export type CropMask = 'circle' | 'rect';

export interface CropBounds {
  /** Output pixels per source pixel at zoom 1. */
  fitScale: number;
  /** The zoom at which the picture just covers the whole frame. Never below 1. */
  coverZoom: number;
  /** The smallest zoom allowed: "fit", or a little less with a circle so the whole picture fits inside it. */
  minZoom: number;
  /** The largest zoom allowed: several times "cover". */
  maxZoom: number;
}

export interface CropBoundsOptions {
  mask?: CropMask;
  /** How far past "cover" the slider goes, as a multiple of it. Default 4. */
  maxCoverMultiple?: number;
}

const EPSILON = 1e-9;

function positive(n: number): number {
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/** Output pixels per source pixel with the whole picture inside the frame. */
export function fitScale(image: Size, frame: Size): number {
  const iw = positive(image.width);
  const ih = positive(image.height);
  if (!iw || !ih) return 1;
  return Math.min(frame.width / iw, frame.height / ih);
}

/** Output pixels per source pixel with the frame completely covered. */
export function coverScale(image: Size, frame: Size): number {
  const iw = positive(image.width);
  const ih = positive(image.height);
  if (!iw || !ih) return 1;
  return Math.max(frame.width / iw, frame.height / ih);
}

/**
 * The zoom at which the whole picture fits inside the circle the frame shows.
 * The circle's diameter is the frame's shorter side, so the picture fits once
 * its diagonal does. Always at most 1 for a square frame.
 */
export function circleFitZoom(image: Size, frame: Size): number {
  const iw = positive(image.width);
  const ih = positive(image.height);
  if (!iw || !ih) return 1;
  const diameter = Math.min(frame.width, frame.height);
  const scale = diameter / Math.hypot(iw, ih);
  return scale / fitScale(image, frame);
}

export function cropBounds(image: Size, frame: Size, options: CropBoundsOptions = {}): CropBounds {
  const fit = fitScale(image, frame);
  const coverZoom = Math.max(1, coverScale(image, frame) / fit);
  const multiple = options.maxCoverMultiple && options.maxCoverMultiple > 1 ? options.maxCoverMultiple : 4;
  const minZoom = options.mask === 'circle' ? Math.min(1, circleFitZoom(image, frame)) : 1;
  return { fitScale: fit, coverZoom, minZoom, maxZoom: coverZoom * multiple };
}

export function clampZoom(zoom: number, bounds: CropBounds): number {
  if (!Number.isFinite(zoom)) return bounds.minZoom;
  return Math.min(bounds.maxZoom, Math.max(bounds.minZoom, zoom));
}

/**
 * How far the picture's centre may move from the frame's centre at a zoom.
 *
 * One rule covers both cases. On an axis where the picture is larger than the
 * frame, it may move until its edge meets the frame's edge, so no gap opens.
 * On an axis where it is smaller (a wide logo zoomed out), it may move until
 * its edge meets the frame's edge from the inside, so none of it is cut off.
 * Either way the limit is half the difference between the two sizes.
 */
export function maxOffset(image: Size, frame: Size, zoom: number, bounds: CropBounds): Point {
  const scale = bounds.fitScale * zoom;
  return {
    x: Math.abs(image.width * scale - frame.width) / 2,
    y: Math.abs(image.height * scale - frame.height) / 2
  };
}

/** The same state, with the zoom inside its range and the picture unable to leave the frame. */
export function clampState(state: CropState, image: Size, frame: Size, bounds: CropBounds): CropState {
  const zoom = clampZoom(state.zoom, bounds);
  const max = maxOffset(image, frame, zoom, bounds);
  const clamp = (v: number, m: number) => (Number.isFinite(v) ? Math.min(m, Math.max(-m, v)) : 0);
  return { zoom, offsetX: clamp(state.offsetX, max.x), offsetY: clamp(state.offsetY, max.y) };
}

export type InitialPlacement = 'auto' | 'fit' | 'cover';

/**
 * Where the picture starts, centred.
 *
 * `auto` covers the frame when the picture is close to the frame's shape (a
 * photo), and fits the whole picture when it is far from it (a wide wordmark,
 * which covering would cut down to its middle letters). "Far" is more than
 * half as wide again, or half as tall again, as the frame's own shape.
 */
export function initialState(
  image: Size, frame: Size, bounds: CropBounds, placement: InitialPlacement = 'auto'
): CropState {
  let zoom: number;
  if (placement === 'cover') zoom = bounds.coverZoom;
  else if (placement === 'fit') zoom = bounds.minZoom;
  else {
    const ratio = (image.width / image.height) / (frame.width / frame.height);
    zoom = Math.max(ratio, 1 / ratio) <= 1.5 ? bounds.coverZoom : bounds.minZoom;
  }
  return clampState({ zoom, offsetX: 0, offsetY: 0 }, image, frame, bounds);
}

/** Move the picture by an amount in output pixels. */
export function panBy(state: CropState, dx: number, dy: number, image: Size, frame: Size, bounds: CropBounds): CropState {
  return clampState({ ...state, offsetX: state.offsetX + dx, offsetY: state.offsetY + dy }, image, frame, bounds);
}

/**
 * Zoom so that the point under `anchor` stays where it is: under the pointer
 * for the wheel, between the fingers for a pinch, the frame's centre for the
 * slider and the keyboard. `anchor` is measured from the frame's centre, in
 * output pixels.
 */
export function zoomAt(
  state: CropState, nextZoom: number, anchor: Point, image: Size, frame: Size, bounds: CropBounds
): CropState {
  const zoom = clampZoom(nextZoom, bounds);
  const ratio = zoom / (state.zoom || bounds.minZoom);
  return clampState({
    zoom,
    offsetX: anchor.x - (anchor.x - state.offsetX) * ratio,
    offsetY: anchor.y - (anchor.y - state.offsetY) * ratio
  }, image, frame, bounds);
}

/**
 * Where the picture is drawn, as a rectangle in output pixels measured from
 * the frame's top-left corner. Parts of it may lie outside the frame.
 */
export function placement(image: Size, frame: Size, state: CropState, bounds: CropBounds): Rect {
  const scale = bounds.fitScale * state.zoom;
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    x: frame.width / 2 + state.offsetX - width / 2,
    y: frame.height / 2 + state.offsetY - height / 2,
    width,
    height
  };
}

export interface CropRects {
  /** The frame, mapped into source pixels. May reach past the picture's edges when zoomed out. */
  frameInSource: Rect;
  /** The part of the picture that lands in the frame, in source pixels. */
  source: Rect;
  /** Where that part lands, in output pixels. Everything else in the frame is background. */
  dest: Rect;
}

/**
 * The two rectangles a canvas needs for `drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh)`.
 * Clipping to the picture first keeps the source rectangle inside the
 * picture, which some browsers require, and keeps the background visible
 * wherever the picture does not reach.
 */
export function cropRects(image: Size, frame: Size, state: CropState, bounds: CropBounds): CropRects {
  const scale = bounds.fitScale * state.zoom;
  const frameInSource: Rect = {
    x: image.width / 2 - (frame.width / 2 + state.offsetX) / scale,
    y: image.height / 2 - (frame.height / 2 + state.offsetY) / scale,
    width: frame.width / scale,
    height: frame.height / scale
  };
  const x0 = Math.max(0, frameInSource.x);
  const y0 = Math.max(0, frameInSource.y);
  const x1 = Math.min(image.width, frameInSource.x + frameInSource.width);
  const y1 = Math.min(image.height, frameInSource.y + frameInSource.height);
  const source: Rect = { x: x0, y: y0, width: Math.max(0, x1 - x0), height: Math.max(0, y1 - y0) };
  const dest: Rect = {
    x: (source.x - frameInSource.x) * scale,
    y: (source.y - frameInSource.y) * scale,
    width: source.width * scale,
    height: source.height * scale
  };
  return { frameInSource, source, dest: snap(dest, frame) };
}

/** Rounding noise must not leave a hairline of background along an edge the picture really reaches. */
function snap(r: Rect, frame: Size): Rect {
  const near = (a: number, b: number) => Math.abs(a - b) < 1e-6;
  let { x, y, width, height } = r;
  if (near(x, 0)) { width += x; x = 0; }
  if (near(y, 0)) { height += y; y = 0; }
  if (near(x + width, frame.width)) width = frame.width - x;
  if (near(y + height, frame.height)) height = frame.height - y;
  return { x, y, width, height };
}

/**
 * How many source pixels the frame spans on its shorter side. This is the
 * honest measure of sharpness: a 640 × 640 result made from 100 source pixels
 * is a 100-pixel picture blown up, however it is padded.
 */
export function sourceEdge(frame: Size, state: CropState, bounds: CropBounds): number {
  const scale = bounds.fitScale * state.zoom;
  return Math.min(frame.width, frame.height) / scale;
}

/**
 * The slider moves on a logarithmic scale, so a step near "fit" and a step
 * near the far end feel the same. 0 is the smallest zoom, 1 the largest.
 */
export function zoomToSlider(zoom: number, bounds: CropBounds): number {
  const span = Math.log(bounds.maxZoom / bounds.minZoom);
  if (span < EPSILON) return 0;
  return Math.min(1, Math.max(0, Math.log(clampZoom(zoom, bounds) / bounds.minZoom) / span));
}

export function sliderToZoom(position: number, bounds: CropBounds): number {
  const p = Math.min(1, Math.max(0, Number.isFinite(position) ? position : 0));
  return clampZoom(bounds.minZoom * Math.pow(bounds.maxZoom / bounds.minZoom, p), bounds);
}

/** Two states that differ by less than a rounding error are the same state. */
export function sameState(a: CropState, b: CropState): boolean {
  return Math.abs(a.zoom - b.zoom) < 1e-6 && Math.abs(a.offsetX - b.offsetX) < 1e-3 && Math.abs(a.offsetY - b.offsetY) < 1e-3;
}
